import type { Coords, Fix } from "@hoa/protocol";
import { bearing, distanceM, move } from "@hoa/geo";

/**
 * Adaptateur de géolocalisation. En PWA, la position n'est disponible que
 * lorsque la page est au premier plan (ch. 9.4) ; en hybride, cette
 * implémentation sera remplacée sans toucher au parcours.
 */

export type GeoError = "refusé" | "indisponible" | "expiré" | "non-supporté";

/* ------------------------------------------------------------------ */
/* Position simulée (développement, ch. 15.4)                          */
/*                                                                     */
/* Un poste fixe n'a souvent aucun fournisseur de position : pas de    */
/* Wi-Fi à scanner, ou service de localisation Windows désactivé. Ce   */
/* mode remplace le capteur par une position que l'on déplace à la     */
/* main, ce qui permet de jouer un parcours entier depuis un PC.       */
/* Il n'est proposé que sur une origine locale (voir estLocal).        */
/* ------------------------------------------------------------------ */

let simule: Coords | null = null;
let cible: Coords | null = null;
let vitesseMs = 8;

/**
 * Origine « de mise au point » : localhost, mais aussi une adresse privée du
 * réseau local. C'est le cas d'un téléphone qui charge l'application depuis le
 * PC sur le Wi-Fi de la maison : sans HTTPS le navigateur refuse la vraie
 * position, la position simulée est alors le seul moyen de jouer le parcours.
 */
export function estLocal(): boolean {
  const h = location.hostname;
  if (h === "localhost" || h === "127.0.0.1" || h === "::1" || h.endsWith(".local")) return true;
  return (
    /^10\./.test(h) ||
    /^192\.168\./.test(h) ||
    /^172\.(1[6-9]|2\d|3[01])\./.test(h)
  );
}

export function simulationActive(): boolean {
  return simule !== null;
}

export function positionSimulee(): Coords | null {
  return simule;
}

export function simuler(coords: Coords | null): void {
  simule = coords;
  cible = null;
}

/** Fait marcher la position simulée vers un point, à vitesse de marche. */
export function marcherVers(destination: Coords | null, metresParSeconde = 8): void {
  cible = destination;
  vitesseMs = metresParSeconde;
}

/** Avance immédiatement de `metres` vers la destination courante ou donnée. */
export function avancer(metres: number, vers?: Coords): void {
  const but = vers ?? cible;
  if (!simule || !but) return;
  const reste = distanceM(simule, but);
  simule = reste <= metres ? { ...but } : move(simule, bearing(simule, but), metres);
}

function fixSimule(): Fix {
  const c = simule!;
  return { lat: c.lat, lon: c.lon, accuracy: 12, at: Date.now() };
}

export function currentFix(timeoutMs = 15_000): Promise<Fix> {
  if (simule) return Promise.resolve(fixSimule());
  return new Promise((resolve, reject) => {
    if (!("geolocation" in navigator)) return reject("non-supporté" as GeoError);
    navigator.geolocation.getCurrentPosition(
      (p) =>
        resolve({
          lat: p.coords.latitude,
          lon: p.coords.longitude,
          accuracy: p.coords.accuracy ?? 999,
          at: Math.round(p.timestamp),
        }),
      (e) => reject(mapError(e)),
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 5_000 },
    );
  });
}

export function watchFix(onFix: (fix: Fix) => void, onError?: (e: GeoError) => void): () => void {
  if (simule) {
    // La position simulée est réémise régulièrement, et progresse d'elle-même
    // si une destination a été fixée : le serveur voit une marche plausible.
    const id = window.setInterval(() => {
      if (!simule) return;
      if (cible) avancer(vitesseMs, cible);
      onFix(fixSimule());
    }, 1000);
    onFix(fixSimule());
    return () => window.clearInterval(id);
  }
  if (!("geolocation" in navigator)) {
    onError?.("non-supporté");
    return () => {};
  }
  const id = navigator.geolocation.watchPosition(
    (p) =>
      onFix({
        lat: p.coords.latitude,
        lon: p.coords.longitude,
        accuracy: p.coords.accuracy ?? 999,
        at: Math.round(p.timestamp),
      }),
    (e) => onError?.(mapError(e)),
    { enableHighAccuracy: true, timeout: 20_000, maximumAge: 2_000 },
  );
  return () => navigator.geolocation.clearWatch(id);
}

function mapError(e: GeolocationPositionError): GeoError {
  if (e.code === e.PERMISSION_DENIED) return "refusé";
  if (e.code === e.TIMEOUT) return "expiré";
  return "indisponible";
}

/**
 * Maintient l'écran allumé pendant l'approche. Sans cela, le verrouillage
 * suspend l'envoi des positions et la tribu ne se voit jamais réunie.
 */
export async function keepAwake(): Promise<() => void> {
  try {
    const anyNav = navigator as Navigator & { wakeLock?: { request: (t: "screen") => Promise<{ release: () => Promise<void> }> } };
    if (!anyNav.wakeLock) return () => {};
    const sentinel = await anyNav.wakeLock.request("screen");
    return () => {
      void sentinel.release();
    };
  } catch {
    return () => {};
  }
}
